import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Send, Loader2, ChevronDown, ChevronUp, Users, MessageCircle, ShieldCheck, Lock, ArrowLeft } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import { useAuth } from '../../context/AuthContext';
import { refusalMessage } from '../../lib/chatRefusal';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// Types
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  experts?: SelectedTwinInfo[];
}

interface SelectedTwinInfo {
  id: number;
  name: string;
  bio?: string;
  tags?: Record<string, string | string[]>;
  reason: string;
}

interface TaskStatus {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE' | 'CANCELLED';
  current?: number;
  total?: number;
  phase?: string;
  message?: string;
  result?: {
    success: boolean;
    selected_twins: SelectedTwinInfo[];
    response: string;
    expert_count: number;
  };
  error?: string;
}

// Custom markdown components for proper rendering
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-gray-900 mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-900 mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-gray-700 mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-gray-700 leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#E8FCFA] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} className="text-[#07E5D1] hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
};

// Expert Tooltip Component
const ExpertTooltip: React.FC<{
  expert: SelectedTwinInfo;
  children: React.ReactNode;
}> = ({ expert, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 400;
      const margin = 20;
      
      let x = rect.left + rect.width / 2;
      const minX = margin + tooltipWidth / 2;
      const maxX = window.innerWidth - margin - tooltipWidth / 2;
      x = Math.max(minX, Math.min(maxX, x));
      
      setPosition({
        x,
        y: rect.top - 8
      });
    }
    setIsVisible(true);
  };

  const initials = expert.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // Format tags for display
  const formatTags = (tags: Record<string, string | string[]> | undefined) => {
    if (!tags) return null;
    const tagItems: string[] = [];
    
    if (tags.overall_experience) {
      tagItems.push(`Experience: ${tags.overall_experience}`);
    }
    if (Array.isArray(tags.key_moments) && tags.key_moments.length > 0) {
      tagItems.push(...tags.key_moments.slice(0, 3));
    }
    
    return tagItems.length > 0 ? tagItems : null;
  };

  const displayTags = formatTags(expert.tags);

  return (
    <div 
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
      className="relative inline-block"
    >
      {children}
      {isVisible && (
        <div 
          className="fixed transform -translate-x-1/2 -translate-y-full pointer-events-none"
          style={{ left: position.x, top: position.y, zIndex: 9999 }}
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[300px] max-w-[400px] mb-2">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-[#07E5D1] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900">{expert.name}</h4>
                <p className="text-xs text-gray-500 mt-0.5">Reddit Birth Experience Persona</p>
              </div>
            </div>
            
            {expert.bio && (
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                {expert.bio.length > 200 ? expert.bio.slice(0, 200) + '...' : expert.bio}
              </p>
            )}
            
            {displayTags && (
              <div className="flex flex-wrap gap-1 mt-2">
                {displayTags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-[#E8FCFA] text-[#07E5D1] text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45" />
        </div>
      )}
    </div>
  );
};

// Experts Accordion Component
const ExpertsAccordion: React.FC<{
  experts: SelectedTwinInfo[];
}> = ({ experts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleCount = 3;
  const hasMore = experts.length > visibleCount;
  const visibleExperts = isExpanded ? experts : experts.slice(0, visibleCount);
  const hiddenCount = experts.length - visibleCount;

  if (experts.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-gray-400" />
        <span className="text-xs text-gray-500 font-medium">
          {experts.length} persona{experts.length !== 1 ? 's' : ''} contributed to this response
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {visibleExperts.map((expert) => (
          <ExpertTooltip key={expert.id} expert={expert}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-[#07E5D1] hover:bg-[#E8FCFA] transition-all cursor-pointer group">
              <div className="w-5 h-5 rounded-full bg-[#07E5D1] flex items-center justify-center text-white text-[9px] font-medium">
                {expert.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <span className="text-xs text-gray-700 group-hover:text-[#07E5D1] font-medium">
                {expert.name}
              </span>
            </div>
          </ExpertTooltip>
        ))}
        
        {hasMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600 font-medium">Show less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600 font-medium">+{hiddenCount} more</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const AskRedditPersonasChat: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [progressMessage, setProgressMessage] = useState('Finding relevant personas...');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Admin check - redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, progressMessage]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Poll for task status
  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const response = await fetch(`${API_URL}/reddit-personas/collective/status/${taskId}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to get task status');
      }
      
      const status: TaskStatus = await response.json();
      
      if (status.state === 'PENDING' || status.state === 'PROGRESS') {
        // Update progress message from backend
        setProgressMessage(status.message || 'Processing...');
      } else if (status.state === 'SUCCESS' && status.result) {
        // Task completed successfully - only process once (guard against duplicate polls)
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: status.result!.response,
            experts: status.result!.selected_twins
          }]);
          
          setIsLoading(false);
          setProgressMessage('Finding relevant personas...');
        }
      } else if (status.state === 'FAILURE') {
        // Task failed - only process once
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`
          }]);
          
          setIsLoading(false);
          setProgressMessage('Finding relevant personas...');
        }
      } else if (status.state === 'CANCELLED') {
        // Task was cancelled - only process once
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          setIsLoading(false);
          setProgressMessage('Finding relevant personas...');
        }
      }
    } catch (error) {
      console.error('Error polling task status:', error);
    }
  }, []);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);
    setStreamingContent('');
    setProgressMessage('Finding relevant personas...');

    try {
      const requestBody = {
        query: input.trim(),
        messages: newMessages.slice(0, -1).map(m => ({ role: m.role, content: m.content }))
      };

      // Submit task (returns immediately with task_id)
      const response = await fetch(`${API_URL}/reddit-personas/collective`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // 403 is a refusal with a reason (the chat cap sends one), not a login problem. Only 401 redirects.
        if (response.status === 401) {
          navigate('/auth');
          return;
        }
        if (response.status === 403) {
          // Rendered HERE rather than thrown. The catch below ignores `error.message` and always writes a
          // hardcoded "Sorry, I encountered an error…", so a thrown refusal never reached the user — the bug this
          // change exists to fix survived in this surface under a different generic message. Caught in review.
          // Awaited BEFORE setMessages: the updater callback is not async, so an inline await there is a
          // syntax error. The suite caught it, which is the argument for the tests below.
          const refusal = await refusalMessage(response);
          setMessages(prev => [...prev, { role: 'assistant', content: refusal }]);
          setIsLoading(false);
          return;
        }
        throw new Error('Failed to start reddit personas chat');
      }

      const data = await response.json();
      const taskId = data.task_id;
      
      if (!taskId) {
        throw new Error('No task ID received');
      }
      
      // Start polling for status every 2 seconds
      pollIntervalRef.current = setInterval(() => {
        pollTaskStatus(taskId);
      }, 2000);
      
      // Also poll immediately
      pollTaskStatus(taskId);

    } catch (error) {
      console.error('Reddit personas chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error starting the consultation. Please try again.' 
      }]);
      setIsLoading(false);
    }
  }, [input, isLoading, messages, pollTaskStatus, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Show access denied for non-admin users
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">Ask Reddit Personas</h1>
              </div>
            </div>
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill Logo" className="h-24" />
            </a>
          </div>
        </header>

        {/* Access Denied Content */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Admin Access Required</h2>
            <p className="text-gray-500 mb-6">
              This feature is only available to administrators. The Ask Reddit Personas chat provides access to birth experience personas derived from Reddit posts and is restricted to authorized personnel only.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go to Dashboard
              </Link>
              <Link
                to="/ask-collective/chat"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 hover:border-[#07E5D1] text-gray-700 hover:text-[#07E5D1] rounded-xl font-medium transition-colors"
              >
                <Users className="w-4 h-4" />
                Try Ask Collective
              </Link>
            </div>
            <p className="text-xs text-gray-400 mt-6">
              If you believe you should have access, please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FEFEFE] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-gray-900">Ask Reddit Personas</h1>
                <span className="px-2 py-0.5 bg-[#E8FCFA] text-[#07E5D1] text-xs font-medium rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  Admin Only
                </span>
              </div>
              <p className="text-xs text-gray-500">Reddit Birth Experience Personas</p>
            </div>
          </div>
          
          {/* Logo - links to home */}
          <a
            href="/"
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img
              src={`${PUBLIC_URL}/logo.png`}
              alt="BluePill Logo"
              className="h-24"
            />
          </a>
        </div>
      </header>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {/* Empty state */}
          {messages.length === 0 && !streamingContent && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-6">
                <MessageCircle className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Ask Reddit Personas</h2>
              <p className="text-gray-500 max-w-lg mx-auto mb-2">
                Ask any question about birth experiences and our AI will select relevant Reddit-derived personas, gather their perspectives, and synthesize insights from real community discussions.
              </p>
              <p className="text-sm text-[#07E5D1] font-medium mb-8">
                Reddit Birth Experience Personas
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  'What were the most common birth complications?',
                  'How did people prepare for their delivery day?',
                  'What advice do mothers give about hospital stays?'
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(suggestion)}
                    className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors shadow-sm"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="space-y-6">
            {messages.map((msg, idx) => (
              <div key={idx}>
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'bg-[#07E5D1] text-white rounded-br-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <>
                        <div className="max-w-none">
                          <ReactMarkdown components={markdownComponents}>{msg.content}</ReactMarkdown>
                        </div>
                        {msg.experts && msg.experts.length > 0 && (
                          <ExpertsAccordion experts={msg.experts} />
                        )}
                      </>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Streaming content */}
            {streamingContent && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl px-5 py-4 bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm">
                  <div className="max-w-none">
                    <ReactMarkdown components={markdownComponents}>{streamingContent}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator */}
            {isLoading && !streamingContent && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
                    <span className="text-sm font-medium text-gray-700">
                      {progressMessage}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 ml-8">
                    This usually takes 1-2 minutes
                  </p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about birth experiences from Reddit..."
              disabled={isLoading}
              className="flex-1 px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-gray-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="p-3.5 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            Powered by AI • Insights from Reddit Birth Experience Personas
          </p>
        </div>
      </div>
    </div>
  );
};

export default AskRedditPersonasChat;



